import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AuthContext } from '../pages/AuthContext';

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 420px;
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(255,255,255,0.15);
  box-shadow: 0 20px 40px rgba(0,0,0,0.35);
`;

/* ================= FORM ================= */

const Title = styled.h2`
  text-align: center;
  margin-bottom: 24px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  text-align: center;
  margin-bottom: 14px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  outline: none;

  &::placeholder {
    color: #cbd5e1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  outline: none;

  option {
    color: #000;
  }
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 12px;
  border-radius: 30px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
`;

/* ================= COMPONENT ================= */

export default function Login() {

  const [form, setForm] = useState({ id: '', password: '', role: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.id || !form.password || !form.role) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://berrybloomer.com/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: form.id,
          password: form.password,
          role: form.role
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();

      // Use role from backend if returned, otherwise fallback to selected role
      const userRole = data.role || form.role;

      /* ================= AUTH CONTEXT LOGIN ================= */

      login(userRole);

      // Save important data
      localStorage.setItem('auth', 'true');
      localStorage.setItem('role', userRole);

      if (data.id) {
        localStorage.setItem('userId', data.id);
      }

      /* ================= ROLE BASED ROUTING ================= */

      switch (userRole) {
        case 'Admin':
        case 'Manager':
          navigate('/pages/dashboard');
          break;
        case 'Sales':
          navigate('/pages/sales-dashboard');
          break;
        case 'Marketing':
          navigate('/pages/marketing-dashboard');
          break;
        case 'Customer':
          navigate('/pages/customer-dashboard');
          break;
        case 'Employee':
          navigate('/pages/employee-dashboard');
          break;
        default:
          navigate('/');
      }

    } catch (err) {
      setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Login</Title>

        {error && <ErrorText>{error}</ErrorText>}

        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            name="id"
            placeholder="User ID"
            value={form.id}
            onChange={handleChange}
            required
          />

          <Input
            type="password"
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
          />

          <Select
            name="role"
            value={form.role}
            onChange={handleChange}
            required
          >
            <option value="">Select Role</option>
            <option value="Admin">Admin</option>
            <option value="Manager">Manager</option>
            <option value="Sales">Sales</option>
            <option value="Marketing">Marketing</option>
            <option value="Customer">Customer</option>
            <option value="Employee">Employee</option>
          </Select>

          <Button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0,196,255,0.45)" }}
            whileTap={{ scale: 0.96 }}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
      </Card>
    </Wrapper>
  );
}